// Fetch-based API client. Reads VITE_API_URL at build time so staging
// and production hit the right backend. Stores session in localStorage
// under `wt_token` / `wt_identity` / `wt_user`.

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:3001";

const TOKEN_KEY = "wt_token";
const IDENTITY_KEY = "wt_identity";
const USER_KEY = "wt_user";

export interface SessionUser {
  email: string;
  display_name: string;
}

export interface AuthResponse {
  token: string;
  identity: string;
  user: SessionUser;
  is_admin: boolean;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getIdentity(): string | null {
  return localStorage.getItem(IDENTITY_KEY);
}

export function getUser(): SessionUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SessionUser;
  } catch {
    return null;
  }
}

export function saveSession(resp: AuthResponse) {
  localStorage.setItem(TOKEN_KEY, resp.token);
  localStorage.setItem(IDENTITY_KEY, resp.identity);
  localStorage.setItem(USER_KEY, JSON.stringify(resp.user));
  void import("../observability/posthog").then((m) =>
    m.identifyCaptain(resp.identity, resp.user.email),
  );
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(IDENTITY_KEY);
  localStorage.removeItem(USER_KEY);
  void import("../observability/posthog").then((m) => m.resetCaptain());
}

import { isCreditsLocked, setCreditsLocked } from "../chrome/CreditLock";
import { isKeyConfigured } from "../chrome/KeyStatus";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const { auth = true, headers, ...rest } = init;
  const finalHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...((headers as Record<string, string>) ?? {}),
  };
  if (auth) {
    const token = getToken();
    if (token) {
      finalHeaders["Authorization"] = `Bearer ${token}`;
    }
  }
  const resp = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: finalHeaders,
  });
  if (!resp.ok) {
    let body: unknown = null;
    try {
      body = await resp.json();
    } catch {
      body = await resp.text();
    }
    // 2026-04-27 — when an authed call comes back 401 the local
    // session is stale (server identity wiped, token expired, secret
    // rotated, etc.). Clearing here means subsequent calls don't
    // keep firing with the dead token — fixes the "presence error
    // on signin before I signed in" symptom where every workspace-
    // scoped subscription kept hitting 401 after a CLEAR_DATA_OK
    // wipe. Skipped on `auth: false` calls (like /auth/start) so a
    // legitimate 401 there doesn't nuke a real session.
    if (resp.status === 401 && auth) {
      clearSession();
    }
    const msg =
      (body as { message?: string } | null)?.message ?? resp.statusText;
    throw new ApiError(resp.status, msg, body);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

// ─── Auth ────────────────────────────────────────────────────────────

export async function authStart(email: string): Promise<{ pending_id: string }> {
  return request("/auth/start", {
    method: "POST",
    auth: false,
    body: JSON.stringify({ email }),
  });
}

export async function authVerifyOtp(
  pending_id: string,
  code: string,
): Promise<AuthResponse> {
  return request("/auth/verify", {
    method: "POST",
    auth: false,
    body: JSON.stringify({ pending_id, code }),
  });
}

export async function authMe(): Promise<{
  identity: string;
  email: string;
  display_name: string;
  is_admin: boolean;
}> {
  return request("/auth/me");
}

// ─── Orgs + Workspaces + Agents + Messages ───────────────────────────
// A01 collapsed the pre-pivot project + mission duality. Everything
// under a workspace is workspace-scoped; `/channels/...` and
// `/projects/...` paths are gone.

// SpacetimeDB SQL results come back as an array of tables, each with { rows: [...column arrays] }.
export type SqlResult = Array<{ rows: unknown[][] }>;

export function extractRows(result: SqlResult): unknown[][] {
  return result[0]?.rows ?? [];
}

/**
 * SpacetimeDB serialises an Identity column as a Product type, so the
 * cell at row[idx] is itself a one-element array holding "0x...". Use
 * this to read it as a bare 64-char hex string (without the 0x prefix),
 * matching how Rust's value_identity_hex / as_identity_hex helpers
 * decode the same shape.
 */
export function decodeIdentity(cell: unknown): string {
  if (Array.isArray(cell)) {
    const inner = cell[0];
    if (typeof inner === "string") {
      return inner.startsWith("0x") ? inner.slice(2) : inner;
    }
  }
  // Defensive fallback: if a future SDB version ever flattens to a bare
  // string, accept it without the 0x prefix. Bare arrays of unexpected
  // shape return empty so callers notice.
  if (typeof cell === "string") {
    return cell.startsWith("0x") ? cell.slice(2) : cell;
  }
  return "";
}

/**
 * SpacetimeDB encodes Sum types (including Option<T>) as a 2-element
 * tuple `[variant_idx, payload]`. Verified live 2026-04-26: Option<T>
 * declares `some` first then `none`, so:
 *   variant 0 = some(value), payload at index 1 holds the wrapped value
 *   variant 1 = none, payload is an empty Product `[]`
 *
 * Returns the wrapped number for `some`, or null for `none`/missing/
 * unexpected shape. Used by panel decoders that read Option<U64> /
 * Option<I64> columns from the raw SqlResult.
 */
export function decodeOptionNumber(cell: unknown): number | null {
  if (Array.isArray(cell) && cell.length >= 2) {
    const variant = cell[0];
    if (variant === 0) {
      const payload = cell[1];
      if (typeof payload === "number") return payload;
      if (typeof payload === "string") {
        const n = Number(payload);
        return Number.isFinite(n) ? n : null;
      }
    }
    if (variant === 1) return null;
  }
  // Defensive fallback: if SDB ever flattens Option<T> to a bare value
  // or null, accept that without breaking.
  if (cell == null) return null;
  if (typeof cell === "number") return cell;
  if (typeof cell === "string") {
    const n = Number(cell);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export async function listOrgs(): Promise<SqlResult> {
  return request("/orgs");
}

export async function renameOrg(
  orgId: number,
  name: string,
): Promise<{ ok: boolean }> {
  return request(`/orgs/${orgId}/rename`, {
    method: "PUT",
    body: JSON.stringify({ name }),
  });
}

export async function listOrgWorkspaces(orgId: number): Promise<SqlResult> {
  return request(`/orgs/${orgId}/workspaces`);
}

export async function createWorkspace(
  orgId: number,
  slug: string,
  name: string,
): Promise<{ ok: boolean }> {
  return request(`/orgs/${orgId}/workspaces`, {
    method: "POST",
    body: JSON.stringify({ slug, name }),
  });
}

export async function deleteWorkspace(workspaceId: number): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}`, { method: "DELETE" });
}

export async function renameWorkspace(
  workspaceId: number,
  name: string,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/rename`, {
    method: "PUT",
    body: JSON.stringify({ name }),
  });
}

export async function deleteMessage(messageId: number): Promise<{ ok: boolean }> {
  return request(`/messages/${messageId}`, { method: "DELETE" });
}

export async function getWorkspace(workspaceId: number): Promise<SqlResult> {
  return request(`/workspaces/${workspaceId}`);
}

export async function listWorkspaceAgents(workspaceId: number): Promise<SqlResult> {
  return request(`/workspaces/${workspaceId}/agents`);
}

export async function listWorkspaceEvents(workspaceId: number): Promise<SqlResult> {
  return request(`/workspaces/${workspaceId}/events`);
}

export async function listTeamRecipeSeats(workspaceId: number): Promise<SqlResult> {
  return request(`/workspaces/${workspaceId}/team-recipe-seats`);
}

// Captain canon 2026-04-26: a persona is one type. trigger_json being
// non-empty makes this row a watcher; otherwise it's a working agent.
// modelBacking can be empty for triggered personas (they ride the
// staff pool today).
export async function createAgent(
  workspaceId: number,
  displayName: string,
  roleTitle: string,
  prompt: string,
  model: string,
  modelBacking: ModelBacking | "",
  triggerJson?: string,
  fireLimitPerTurn?: number,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/agents`, {
    method: "POST",
    body: JSON.stringify({
      display_name: displayName,
      role_title: roleTitle,
      prompt,
      model,
      model_backing: modelBacking,
      trigger_json: triggerJson ?? null,
      fire_limit_per_turn: fireLimitPerTurn ?? null,
    }),
  });
}

export async function deleteAgent(agentId: number): Promise<{ ok: boolean }> {
  return request(`/agents/${agentId}`, { method: "DELETE" });
}

export async function setAgentModel(
  agentId: number,
  model: string,
  modelBacking?: ModelBacking,
): Promise<{ ok: boolean }> {
  return request(`/agents/${agentId}/model`, {
    method: "PUT",
    body: JSON.stringify({
      model,
      model_backing: modelBacking ?? null,
    }),
  });
}

// Captain canon 2026-04-26: createWatcher retired. Pass trigger_json
// + fire_limit_per_turn to createAgent — same row, same reducer.

export async function setAgentMuted(
  agentId: number,
  muted: boolean,
): Promise<{ ok: boolean }> {
  return request(`/agents/${agentId}/mute`, {
    method: "PUT",
    body: JSON.stringify({ muted }),
  });
}

export async function setAgentTrigger(
  agentId: number,
  triggerJson: string,
): Promise<{ ok: boolean }> {
  return request(`/agents/${agentId}/trigger`, {
    method: "PUT",
    body: JSON.stringify({ trigger_json: triggerJson }),
  });
}

// A09: Road tab CRUD.

export async function createRoad(
  workspaceId: number,
  name: string,
  kind: "main" | "parallel",
  parent_road_id?: number,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/roads`, {
    method: "POST",
    body: JSON.stringify({ name, kind, parent_road_id: parent_road_id ?? 0 }),
  });
}

export async function renameRoad(
  roadId: number,
  name: string,
): Promise<{ ok: boolean }> {
  return request(`/roads/${roadId}/rename`, {
    method: "PUT",
    body: JSON.stringify({ name }),
  });
}

export async function deleteRoad(roadId: number): Promise<{ ok: boolean }> {
  return request(`/roads/${roadId}`, { method: "DELETE" });
}

export async function createAgenda(
  workspaceId: number,
  road_id: number,
  title: string,
  objective: string,
  order_index: number,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/agendas`, {
    method: "POST",
    body: JSON.stringify({ road_id, title, objective, order_index }),
  });
}

export async function deleteAgenda(agendaId: number): Promise<{ ok: boolean }> {
  return request(`/agendas/${agendaId}`, { method: "DELETE" });
}

export interface CreateStepRequest {
  road_id: number;
  agenda_id: number;
  title: string;
  objective: string;
  order_index: number;
  predecessor_ids_json?: string;
}

export async function createStep(
  workspaceId: number,
  req: CreateStepRequest,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/steps`, {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export async function setStepStatus(
  stepId: number,
  status: "open" | "in_progress" | "closed",
): Promise<{ ok: boolean }> {
  return request(`/steps/${stepId}/status`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
}

export async function setStepOrder(
  stepId: number,
  order_index: number,
): Promise<{ ok: boolean }> {
  return request(`/steps/${stepId}/order`, {
    method: "PUT",
    body: JSON.stringify({ order_index }),
  });
}

export async function deleteStep(stepId: number): Promise<{ ok: boolean }> {
  return request(`/steps/${stepId}`, { method: "DELETE" });
}

export async function createGate(
  workspaceId: number,
  agenda_id: number,
  expected_outputs_json: string,
  severity: "minor" | "major" | "critical",
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/gates`, {
    method: "POST",
    body: JSON.stringify({
      agenda_id,
      expected_outputs_json,
      severity,
    }),
  });
}

export async function setGateStatus(
  gateId: number,
  status: "pending" | "passed" | "failed" | "revised",
  actual_evidence_refs_json = "[]",
): Promise<{ ok: boolean }> {
  return request(`/gates/${gateId}/status`, {
    method: "PUT",
    body: JSON.stringify({
      status,
      actual_evidence_refs_json,
    }),
  });
}

// A10: recipes.

export interface CreateRecipeBody {
  name: string;
  notes?: string;
  autonomy_level?: string;
  origin_workspace_id: number;
}

export async function listRecipes(orgId: number): Promise<SqlResult> {
  return request(`/orgs/${orgId}/recipes`);
}

export async function createRecipe(
  orgId: number,
  body: CreateRecipeBody,
): Promise<{ ok: boolean }> {
  return request(`/orgs/${orgId}/recipes`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function forkRecipe(
  recipeId: number,
  targetWorkspaceId: number,
): Promise<{ ok: boolean }> {
  return request(`/recipes/${recipeId}/fork`, {
    method: "POST",
    body: JSON.stringify({ target_workspace_id: targetWorkspaceId }),
  });
}

export async function deleteRecipe(recipeId: number): Promise<{ ok: boolean }> {
  return request(`/recipes/${recipeId}`, { method: "DELETE" });
}

export async function listRecipeUse(workspaceId: number): Promise<SqlResult> {
  return request(`/workspaces/${workspaceId}/recipe-use`);
}

// A10: persona score history.

export async function listPersonaScores(personaId: number): Promise<SqlResult> {
  return request(`/personas/${personaId}/scores`);
}

// A13: workspace-scoped provider keys.

export async function listWorkspaceProviderKeys(
  workspaceId: number,
): Promise<ProviderKeyListResponse> {
  return request(`/workspaces/${workspaceId}/provider-keys`);
}

export async function setWorkspaceProviderKey(
  workspaceId: number,
  provider: string,
  key: string,
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/provider-keys/${encodeURIComponent(provider)}`,
    { method: "PUT", body: JSON.stringify({ key }) },
  );
}

export async function revokeWorkspaceProviderKey(
  workspaceId: number,
  provider: string,
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/provider-keys/${encodeURIComponent(provider)}`,
    { method: "DELETE" },
  );
}

// A18: CI/CD webhook URL + results.

export interface CicdWebhookUrlResponse {
  url: string;
  secret: string;
  configured: boolean;
}

export async function getCicdWebhookUrl(
  workspaceId: number,
): Promise<CicdWebhookUrlResponse> {
  return request(`/workspaces/${workspaceId}/cicd-webhook-url`);
}

export async function rotateCicdWebhook(
  workspaceId: number,
): Promise<CicdWebhookUrlResponse> {
  return request(`/workspaces/${workspaceId}/cicd-webhook-url/rotate`, {
    method: "POST",
  });
}

export async function revokeCicdWebhook(
  workspaceId: number,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/cicd-webhook-url`, {
    method: "DELETE",
  });
}

// A17: collab — invites, threads, members.

export async function listOrgInvites(orgId: number): Promise<SqlResult> {
  return request(`/orgs/${orgId}/invites`);
}

export async function createOrgInvite(
  orgId: number,
  email: string,
  role = "collaborator",
): Promise<{ ok: boolean }> {
  return request(`/orgs/${orgId}/invites`, {
    method: "POST",
    body: JSON.stringify({ email, role }),
  });
}

export async function getInvitePublic(
  token: string,
): Promise<{ org_id: number; org_name: string; role: string; accepted_at: number }> {
  return request(`/invites/${encodeURIComponent(token)}`, { auth: false });
}

export async function acceptInvite(token: string): Promise<{ ok: boolean }> {
  return request(`/invites/${encodeURIComponent(token)}/accept`, {
    method: "POST",
  });
}

export async function listWorkspaceMembers(
  workspaceId: number,
): Promise<SqlResult> {
  return request(`/workspaces/${workspaceId}/members`);
}

export async function addWorkspaceMember(
  workspaceId: number,
  user_identity: string,
  role = "collaborator",
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/members`, {
    method: "POST",
    body: JSON.stringify({ user_identity, role }),
  });
}

export async function removeWorkspaceMember(
  workspaceId: number,
  user_identity: string,
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/members/${encodeURIComponent(user_identity)}`,
    { method: "DELETE" },
  );
}

export async function listThreads(workspaceId: number): Promise<SqlResult> {
  return request(`/workspaces/${workspaceId}/threads`);
}

export async function createThread(
  workspaceId: number,
  kind: "side_with_seat" | "group" | "dm",
  title: string,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/threads`, {
    method: "POST",
    body: JSON.stringify({ kind, title }),
  });
}

export async function listThreadMessages(
  threadId: number,
): Promise<SqlResult> {
  return request(`/threads/${threadId}/messages`);
}

export async function postThreadMessage(
  threadId: number,
  body: string,
): Promise<{ ok: boolean }> {
  return request(`/threads/${threadId}/messages`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
}

// A14: Pearl persona candidate craft.

export interface PersonaCandidate {
  candidate_id: number;
  card_json: unknown;
}

export async function generatePersonaCandidates(
  orgId: number,
  role_description: string,
  axis_modifier = "",
  parent_candidate_id = 0,
  count = 4,
): Promise<{ candidates: PersonaCandidate[] }> {
  return request(`/orgs/${orgId}/persona-candidates`, {
    method: "POST",
    body: JSON.stringify({
      role_description,
      axis_modifier,
      parent_candidate_id,
      count,
    }),
  });
}

/**
 * A15 clone-working-style: ask Pearl to mirror the Captain's voice
 * into N candidate cards using their recent decisions + chat
 * messages as substrate.
 */
export async function clonePersonaFromCaptain(
  workspaceId: number,
  axis_modifier = "",
  count = 4,
): Promise<{ candidates: PersonaCandidate[] }> {
  return request(
    `/workspaces/${workspaceId}/persona-candidates/clone-captain`,
    {
      method: "POST",
      body: JSON.stringify({ axis_modifier, count }),
    },
  );
}

export type ModelBacking =
  | "openrouter_key"
  | "anthropic_key"
  | "openai_key"
  | "google_key"
  | "subscription"
  | "pooled";

export async function applyPersonaCandidate(
  workspaceId: number,
  candidateId: number,
  model: string,
  model_backing: ModelBacking,
  kind: "working" | "watcher" = "working",
  trigger_json = "",
  fire_limit_per_turn = 1,
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/agents/from-candidate/${candidateId}`,
    {
      method: "POST",
      body: JSON.stringify({
        model,
        model_backing,
        kind,
        trigger_json,
        fire_limit_per_turn,
      }),
    },
  );
}

// A15: user score history (self only in v1).

export async function listUserScores(identityHex: string): Promise<SqlResult> {
  return request(`/users/${encodeURIComponent(identityHex)}/scores`);
}

export async function listPersonaScoreAggregate(
  personaId: number,
): Promise<SqlResult> {
  return request(`/personas/${personaId}/scores/aggregate`);
}

export async function listWorkspaceScoreDeltas(
  workspaceId: number,
): Promise<SqlResult> {
  return request(`/workspaces/${workspaceId}/score-deltas`);
}

// A11: retro note + road convergence.

export async function addRetroNote(
  workspaceId: number,
  entryId: number,
  note: string,
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/ledger-curated/${entryId}/retro-note`,
    { method: "POST", body: JSON.stringify({ note }) },
  );
}

export async function convergeRoad(
  workspaceId: number,
  roadId: number,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/roads/${roadId}/converge`, {
    method: "POST",
  });
}

export async function listWorkspaceMessages(workspaceId: number): Promise<SqlResult> {
  return request(`/workspaces/${workspaceId}/messages`);
}

export async function postMessage(
  workspaceId: number,
  body: string,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/messages`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
}

// ─── Repo connections + GitHub OAuth (A01) ──────────────────────────

export async function listWorkspaceRepoConnections(
  workspaceId: number,
): Promise<SqlResult> {
  return request(`/workspaces/${workspaceId}/repo-connections`);
}

export interface GithubRepo {
  id: number;
  name: string;
  full_name: string;
  default_branch: string;
  private: boolean;
}

export async function listGithubRepos(workspaceId: number): Promise<GithubRepo[]> {
  return request(`/github/repos?workspace_id=${workspaceId}`);
}

export async function connectGithubToken(
  workspaceId: number,
  token: string,
): Promise<{ ok: boolean; login: string }> {
  return request(`/workspaces/${workspaceId}/github-token`, {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export async function attachRepo(
  workspaceId: number,
  githubOwner: string,
  githubRepo: string,
  defaultBranch: string,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/repos`, {
    method: "POST",
    body: JSON.stringify({
      github_owner: githubOwner,
      github_repo: githubRepo,
      default_branch: defaultBranch,
    }),
  });
}

export async function detachRepo(repoConnectionId: number): Promise<{ ok: boolean }> {
  return request(`/repo-connections/${repoConnectionId}`, { method: "DELETE" });
}

export async function setRepoCommitAuthor(
  repoConnectionId: number,
  name: string,
  email: string,
): Promise<{ ok: boolean }> {
  return request(`/repo-connections/${repoConnectionId}/commit-author`, {
    method: "PUT",
    body: JSON.stringify({ name, email }),
  });
}

/** Mint the GitHub OAuth authorize URL via authed POST. The frontend
 * then sets `window.location.href = url`. The previous GET-with-Bearer
 * pattern broke under <a> navigation (browser drops the auth header
 * on top-level navigation) — POST keeps the Bearer header on the
 * mint, then a plain redirect carries the OAuth `state` to GitHub. */
export async function startGithubOauth(workspaceId: number): Promise<{ url: string }> {
  return request<{ url: string }>("/github/oauth/start", {
    method: "POST",
    body: JSON.stringify({ workspace_id: workspaceId }),
  });
}

// ─── A03: autonomy + A04: model access ─────────────────────────────

export type AutonomyLevel =
  | "freeform"
  | "paused_on_commit"
  | "paused_on_destructive";

export async function setWorkspaceAutonomy(
  workspaceId: number,
  level: AutonomyLevel,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/autonomy`, {
    method: "PUT",
    body: JSON.stringify({ autonomy_level: level }),
  });
}

export type ModelAccessProvider =
  | "openrouter"
  | "claude_subscription"
  | "codex_subscription";

export type ModelAccessStatus =
  | "pending"
  | "connected"
  | "needs_reauth"
  | "revoked";

export async function listModelAccess(workspaceId: number): Promise<SqlResult> {
  return request(`/workspaces/${workspaceId}/model-access`);
}

// A07: approve / reject pending commits + resolve needs_human_input.

export async function approvePendingCommit(
  workspaceId: number,
  pendingCommitId: number,
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/pending-commits/${pendingCommitId}/approve`,
    { method: "POST" },
  );
}

export async function rejectPendingCommit(
  workspaceId: number,
  pendingCommitId: number,
  note?: string,
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/pending-commits/${pendingCommitId}/reject`,
    {
      method: "POST",
      body: JSON.stringify({ note }),
    },
  );
}

export async function resolveHumanInput(
  workspaceId: number,
  attachmentId: number,
  value: string,
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/human-input/${attachmentId}/resolve`,
    { method: "POST", body: JSON.stringify({ value }) },
  );
}

export async function setWorkspaceDigestHour(
  workspaceId: number,
  hour: number,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/digest-hour`, {
    method: "PUT",
    body: JSON.stringify({ hour_local: hour }),
  });
}

export async function setWorkspaceEscalationEmail(
  workspaceId: number,
  email: string,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/escalation-email`, {
    method: "PUT",
    body: JSON.stringify({ email }),
  });
}

// Vela-led onboarding config-state. Vela reads this to know what's
// already configured + what to ask for next; the inline `needs_config`
// attachment form posts to the existing per-kind setters above.

export interface ConfigStateProviderKey {
  provider: string;
  configured: boolean;
  scope: string;
  last4: string | null;
}

export interface ConfigStateRepo {
  repo_connection_id: number;
  github_owner: string;
  github_repo: string;
  default_branch: string;
  commit_author_name: string | null;
  commit_author_email: string | null;
}

export interface ConfigStateAgent {
  agent_id: number;
  seat_key: string;
  display_name: string;
  kind: string;
}

export interface ConfigStateResponse {
  workspace_id: number;
  org_id: number;
  workspace_name: string;
  autonomy_level: string;
  digest_at_hour_local: number;
  escalation_contact_email: string;
  sandbox_state: string;
  provider_keys: ConfigStateProviderKey[];
  repo_connections: ConfigStateRepo[];
  agents: ConfigStateAgent[];
  has_main_road: boolean;
  agenda_count: number;
  step_count: number;
}

export async function getConfigState(
  workspaceId: number,
): Promise<ConfigStateResponse> {
  return request(`/workspaces/${workspaceId}/config-state`);
}

export async function setModelAccess(
  workspaceId: number,
  provider: ModelAccessProvider,
  status: ModelAccessStatus,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/model-access`, {
    method: "POST",
    body: JSON.stringify({ provider, status }),
  });
}

export async function revokeModelAccess(
  workspaceId: number,
  provider: ModelAccessProvider,
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/model-access/${encodeURIComponent(provider)}`,
    {
      method: "DELETE",
    },
  );
}

// ─── A06: sandbox files + commit diff ──────────────────────────────

export interface SandboxDirEntry {
  name: string;
  kind: "file" | "directory";
  size: number;
}

export type SandboxFileResponse =
  | { kind: "directory"; path: string; entries: SandboxDirEntry[] }
  | { kind: "file"; path: string; contents: string }
  | { kind: "binary"; path: string; size: number };

export async function readWorkspaceFile(
  workspaceId: number,
  path: string,
): Promise<SandboxFileResponse> {
  const qs = path ? `?path=${encodeURIComponent(path)}` : "";
  return request(`/workspaces/${workspaceId}/files${qs}`);
}

export interface CommitDiffResponse {
  sha: string;
  repo: string;
  diff: string;
}

export async function getCommitDiff(
  workspaceId: number,
  sha: string,
  repo: string,
): Promise<CommitDiffResponse> {
  return request(
    `/workspaces/${workspaceId}/commits/${encodeURIComponent(sha)}/diff?repo=${encodeURIComponent(repo)}`,
  );
}

// ─── Sandbox (A02) ──────────────────────────────────────────────────

export interface SandboxStatusResponse {
  configured: boolean;
  sandbox_id: string;
  state: string;
  last_heartbeat: number;
}

export async function getWorkspaceSandbox(
  workspaceId: number,
): Promise<SandboxStatusResponse> {
  return request(`/workspaces/${workspaceId}/sandbox`);
}

export async function wakeWorkspaceSandbox(
  workspaceId: number,
): Promise<{ ok: boolean; sandbox_id: string; state: string }> {
  return request(`/workspaces/${workspaceId}/sandbox/wake`, {
    method: "POST",
  });
}

/// Realtime details fetched live from Daytona — only populated when a
/// sandbox is provisioned and the staff key is configured. Empty
/// strings / null fields when not yet available.
export interface SandboxDetailsResponse {
  id: string;
  state: string;
  cpu: number | null;
  gpu: number | null;
  memory: number | null;
  disk: number | null;
  class: string | null;
  target: string | null;
  lastActivityAt: string | null;
  createdAt: string | null;
  snapshotName: string | null;
  errorReason: string | null;
  autoStopInterval: number | null;
  autoArchiveInterval: number | null;
  /** A33 slice B — Daytona per-port preview URL for the noVNC display
   * surface. Null when the sandbox is asleep / port not bound / Daytona
   * returns an error. SandboxTab renders an iframe when present;
   * placeholder otherwise. */
  previewUrl: string | null;
  /** True when port 6080 inside the sandbox is actually serving noVNC.
   * The previewUrl exists as soon as Daytona has a sandbox, but the
   * port doesn't bind until computer-use finishes starting up — so
   * `previewUrl !== null` is a necessary-but-not-sufficient signal.
   * Backend probes the URL and sets this to true on 2xx. SandboxTab
   * gates iframe mount on this so the iframe never lands on a 502
   * page that wouldn't auto-recover. */
  vncReady: boolean;
}

export async function getWorkspaceSandboxDetails(
  workspaceId: number,
): Promise<SandboxDetailsResponse> {
  return request(`/workspaces/${workspaceId}/sandbox/details`);
}

// ─── Sandbox custom-preview-proxy (2026-04-27) ─────────────────────
// The iframe in SandboxTab loads through a backend proxy that
// injects Daytona's X-Daytona-Skip-Preview-Warning header so the
// noVNC display shows up immediately without the warning interstitial
// (iframes can't add custom headers; only a server-side proxy can).
// Proxy auth is an HMAC-signed token bound to (workspace_id, exp);
// minted from a JWT-authed endpoint, then carried in the iframe URL
// prefix so all sub-resource + WebSocket requests include it.

export interface SandboxProxyMint {
  token: string;
  port: number;
}

export async function mintSandboxProxyToken(
  workspaceId: number,
): Promise<SandboxProxyMint> {
  return request(`/workspaces/${workspaceId}/sandbox-proxy/mint`, {
    method: "POST",
  });
}

/// Push the iframe's actual pixel dimensions to the backend so xrandr
/// can switch the sandbox's Xvfb mode in-place. Returns the dims the
/// backend actually applied (rounded to nearest 8 px and clamped to
/// the framebuffer max). SandboxTab calls this on mount and on every
/// container resize (debounced).
export async function setSandboxResolution(
  workspaceId: number,
  width: number,
  height: number,
): Promise<{ ok: boolean; width: number; height: number }> {
  return request(`/workspaces/${workspaceId}/sandbox/resolution`, {
    method: "POST",
    body: JSON.stringify({ width, height }),
  });
}

/** Match the XFCE desktop wallpaper to the Waytide theme bg.
 * x11vnc requires a backdrop colour at the X-server level; we can't
 * make the root window transparent, but matching the surface color
 * exactly lets the desktop blend with the surrounding chrome. The
 * frontend POSTs on iframe mount and again on every theme switch
 * (MutationObserver on <html style>). */
export async function setSandboxDesktopColor(
  workspaceId: number,
  r: number,
  g: number,
  b: number,
): Promise<{ ok: boolean; r: number; g: number; b: number }> {
  return request(`/workspaces/${workspaceId}/sandbox/desktop-color`, {
    method: "POST",
    body: JSON.stringify({ r, g, b }),
  });
}

export function buildSandboxProxyUrl(token: string, port: number): string {
  // noVNC inside the daytonaio/sandbox snapshot is served by Python's
  // http.server, which doesn't auto-redirect / to vnc.html — leaving
  // the iframe staring at a directory listing of the noVNC source tree
  // (verified via curl 2026-04-27). vnc.html is the canonical entry
  // point; ?autoconnect=true opens the WebSocket back through this
  // same proxy origin without a manual "Connect" click. resize=scale
  // does pure CLIENT-SIDE scaling — the canvas is rendered locally to
  // fit the iframe viewport. resize=remote (server-side resize) is
  // refused by Daytona's noVNC config with "Resize is administratively
  // prohibited" (verified in browser console 2026-04-27); scale is the
  // only mode that fits the desktop without requiring server
  // cooperation. WebSocket sub-resource (websockify) routes through
  // sandbox_proxy_routes' WS bridge automatically.
  return `${API_BASE}/sandbox-proxy/${token}/${port}/vnc.html?autoconnect=true&resize=scale`;
}

// ─── Presence (A38) ────────────────────────────────────────────────

export type Presence = "present_active" | "present_idle" | "absent";

export interface PresenceMeResponse {
  presence: Presence;
  last_active_at: number;
  last_input_at: number;
}

export async function postWorkspacePresenceHeartbeat(
  workspaceId: number,
  isInputEvent: boolean,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/presence`, {
    method: "POST",
    body: JSON.stringify({ is_input_event: isInputEvent }),
  });
}

export async function getWorkspacePresenceMe(
  workspaceId: number,
): Promise<PresenceMeResponse> {
  return request(`/workspaces/${workspaceId}/presence/me`);
}

// ─── Provider keys (BYOK) ───────────────────────────────────────────

export interface ProviderKeySummary {
  provider: string;
  last4: string;
  updated_at: number;
}

export interface ProviderKeyListResponse {
  keys: ProviderKeySummary[];
}

export async function listProviderKeys(
  orgId: number,
): Promise<ProviderKeyListResponse> {
  return request(`/orgs/${orgId}/provider-keys`);
}

export async function setProviderKey(
  orgId: number,
  provider: string,
  key: string,
): Promise<{ ok: boolean }> {
  return request(`/orgs/${orgId}/provider-keys/${encodeURIComponent(provider)}`, {
    method: "PUT",
    body: JSON.stringify({ key }),
  });
}

export async function revokeProviderKey(
  orgId: number,
  provider: string,
): Promise<{ ok: boolean }> {
  return request(`/orgs/${orgId}/provider-keys/${encodeURIComponent(provider)}`, {
    method: "DELETE",
  });
}

// ─── Usage + Limits ──────────────────────────────────────────────────

export interface UsageWindow {
  cost_usd_micros: number;
  input_tokens: number;
  output_tokens: number;
  turns: number;
  remote_env_minutes: number;
  remote_env_cost_usd_micros: number;
  resend_messages: number;
}

export interface DailyPoint {
  day: number;
  cost_usd_micros: number;
}

export interface UsageResponse {
  today: UsageWindow;
  week: UsageWindow;
  month: UsageWindow;
  daily_breakdown: DailyPoint[];
  top_model: string | null;
}

export interface LimitsResponse {
  per_turn_max_tokens: number;
  daily_cost_cap_micros: number;
  monthly_cost_cap_micros: number;
  soft_limit: boolean;
}

export async function getOrgUsage(orgId: number): Promise<UsageResponse> {
  return request(`/orgs/${orgId}/usage`);
}

export async function getWorkspaceUsage(
  workspaceId: number,
): Promise<UsageResponse> {
  return request(`/workspaces/${workspaceId}/usage`);
}

export async function getOrgLimits(orgId: number): Promise<LimitsResponse> {
  return request(`/orgs/${orgId}/limits`);
}

export async function setOrgLimits(
  orgId: number,
  limits: LimitsResponse,
): Promise<{ ok: boolean }> {
  return request(`/orgs/${orgId}/limits`, {
    method: "PUT",
    body: JSON.stringify(limits),
  });
}

export interface SystemStatus {
  provider_key_encryption_configured: boolean;
  orchestrator_shared_secret_configured: boolean;
  r2_configured: boolean;
  github_token_configured: boolean;
  resend_configured: boolean;
}

export async function getSystemStatus(): Promise<SystemStatus> {
  return request(`/system-status`, { auth: false });
}

// Avatar re-roll.
export async function regenerateAgentAvatar(
  agentId: number,
  orgId: number,
): Promise<{ ok: boolean }> {
  requireCreditsUnlocked(orgId);
  requireOpenRouterKey(orgId);
  return spendRequest(orgId, () =>
    request(`/agents/${agentId}/avatar/regenerate`, {
      method: "POST",
    }),
  );
}

// ─── Ephemeral chat (Council + arbitrary model) ──────────────────────

export interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

export interface ChatResponse {
  role: "assistant";
  content: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  cost_usd_micros: number;
  duration_ms: number;
}

export type CouncilSeat =
  | "pearl"
  | "maris"
  | "vela"
  | "shore"
  | "compass"
  | "coral";

export function requireCreditsUnlocked(orgId: number | null | undefined): void {
  if (orgId != null && isCreditsLocked(orgId)) {
    throw new ApiError(
      423,
      "Credits are locked. Unlock from the balance popout to send.",
      { code: "credits_locked" },
    );
  }
}

export function requireOpenRouterKey(orgId: number | null | undefined): void {
  if (orgId == null) return;
  if (isKeyConfigured(orgId) === false) {
    throw new ApiError(428, "no_openrouter_key", {
      code: "no_openrouter_key",
    });
  }
}

async function spendRequest<T>(
  orgId: number | null | undefined,
  fn: () => Promise<T>,
): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    if (
      e instanceof ApiError
      && e.status === 402
      && orgId != null
      && (e.body as { code?: string } | null)?.code === "org_spend_cap_exceeded"
    ) {
      setCreditsLocked(orgId, true);
    }
    throw e;
  }
}

export async function councilChat(
  seat: CouncilSeat,
  orgId: number,
  messages: ChatMessage[],
  model?: string,
): Promise<ChatResponse> {
  requireCreditsUnlocked(orgId);
  requireOpenRouterKey(orgId);
  return spendRequest(orgId, () =>
    request(`/chat/council/${seat}`, {
      method: "POST",
      body: JSON.stringify({ org_id: orgId, messages, model }),
    }),
  );
}

export async function modelChat(
  orgId: number,
  model: string,
  messages: ChatMessage[],
  system?: string,
): Promise<ChatResponse> {
  requireCreditsUnlocked(orgId);
  requireOpenRouterKey(orgId);
  return spendRequest(orgId, () =>
    request(`/chat/model`, {
      method: "POST",
      body: JSON.stringify({ org_id: orgId, model, messages, system }),
    }),
  );
}

export interface OpenRouterCreditsResponse {
  configured: boolean;
  total_credits: number;
  total_usage: number;
  remaining: number;
}

export async function openRouterCredits(
  orgId: number,
): Promise<OpenRouterCreditsResponse> {
  return request(`/orgs/${orgId}/openrouter-credits`);
}

// =====================================================================
// A20 — Persona Runtime Contracts
// =====================================================================

export interface PersonaContractRow {
  agent_id: number;
  workspace_id: number;
  remit: string;
  task_tags_json: string;
  model_lanes_json: string;
  skills_json: string;
  tools_json: string;
  memory_privileges_json: string;
  trigger_contract_json: string;
  reaction_contract_json: string;
  evidence_contract_json: string;
  rubric_json: string;
  character_budget: string;
  safety_constraints_json: string;
  version: number;
  created_at: number;
  updated_at: number;
}

export interface UpsertPersonaContractRequest {
  remit: string;
  task_tags: string[];
  model_lanes: Record<string, unknown>;
  skills: unknown[];
  tools: unknown[];
  memory_privileges: Record<string, unknown>;
  trigger_contract: Record<string, unknown>;
  reaction_contract: Record<string, unknown>;
  evidence_contract: Record<string, unknown>;
  rubric: Record<string, unknown>;
  character_budget: "minimal" | "working" | "expressive";
  safety_constraints: Record<string, unknown>;
}

export async function listPersonaContracts(
  workspaceId: number,
): Promise<PersonaContractRow[]> {
  return request(`/workspaces/${workspaceId}/persona-contracts`);
}

export async function upsertPersonaContract(
  workspaceId: number,
  agentId: number,
  body: UpsertPersonaContractRequest,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/agents/${agentId}/contract`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export async function deletePersonaContract(
  workspaceId: number,
  agentId: number,
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/agents/${agentId}/contract`, {
    method: "DELETE",
  });
}

// =====================================================================
// A22 — Work Receipts + Gate Evidence
// =====================================================================

export interface ReceiptRow {
  receipt_id: number;
  workspace_id: number;
  kind: string;
  subject_kind: string;
  subject_id: number;
  title: string;
  body_md: string;
  evidence_refs_json: string;
  author_agent_id: number | null;
  task_tags_json: string;
  cost_micros: number;
  score_delta_json: string;
  superseded_by: number | null;
  created_at: number;
}

export async function listReceipts(
  workspaceId: number,
  kind?: string,
  limit?: number,
): Promise<ReceiptRow[]> {
  const params = new URLSearchParams();
  if (kind) params.set("kind", kind);
  if (limit) params.set("limit", String(limit));
  const qs = params.toString();
  // Backend returns the SDB SqlResult shape; column order matches the
  // SELECT in receipts_routes::list_receipts. author_agent_id and
  // superseded_by are Option<U64> — decode via decodeOptionNumber.
  const raw = await request<SqlResult>(
    `/workspaces/${workspaceId}/receipts${qs ? `?${qs}` : ""}`,
  );
  return extractRows(raw).map((r) => ({
    receipt_id: Number(r[0] ?? 0),
    workspace_id: Number(r[1] ?? 0),
    kind: String(r[2] ?? ""),
    subject_kind: String(r[3] ?? ""),
    subject_id: Number(r[4] ?? 0),
    title: String(r[5] ?? ""),
    body_md: String(r[6] ?? ""),
    evidence_refs_json: String(r[7] ?? "[]"),
    author_agent_id: decodeOptionNumber(r[8]),
    task_tags_json: String(r[9] ?? "[]"),
    cost_micros: Number(r[10] ?? 0),
    score_delta_json: String(r[11] ?? "{}"),
    superseded_by: decodeOptionNumber(r[12]),
    created_at: Number(r[13] ?? 0),
  }));
}

export async function generateGateReceipt(
  workspaceId: number,
  stepId: number,
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/receipts/generate/gate/${stepId}`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export async function generateCommitReceipt(
  workspaceId: number,
  messageId: number,
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/receipts/generate/commit/${messageId}`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export async function generateTurnReceipt(
  workspaceId: number,
  turnId: number,
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/receipts/generate/turn/${turnId}`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export async function supersedeReceipt(
  workspaceId: number,
  receiptId: number,
  body: {
    body_md: string;
    evidence_refs: unknown;
    score_delta: unknown;
  },
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/receipts/${receiptId}/supersede`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export async function attachReceiptNarration(
  workspaceId: number,
  receiptId: number,
  body: {
    script: string;
    artefact_url?: string;
    kind: "audio_script" | "video_script";
  },
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/receipts/${receiptId}/narration`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export async function prExportReceipt(
  workspaceId: number,
  receiptId: number,
): Promise<{ markdown: string }> {
  return request(`/workspaces/${workspaceId}/receipts/${receiptId}/pr-export`);
}

// =====================================================================
// A21 — Shadow Teams + Real-Work Evaluation
// =====================================================================

export interface TaskTagRow {
  tag_id: number;
  subject_kind: string;
  subject_id: number;
  tag: string;
  source: string;
  created_at: number;
}

export interface OutcomeTagRow {
  tag_id: number;
  subject_kind: string;
  subject_id: number;
  outcome: string;
  source: string;
  created_at: number;
}

export interface ShadowRunRow {
  shadow_run_id: number;
  source_kind: string;
  source_id: number;
  harness: string;
  budget_micros: number;
  status: string;
  spent_micros: number;
  receipt_ids_json: string;
  created_at: number;
  updated_at: number;
  completed_at: number | null;
  error: string | null;
}

export interface RecipeScorecardRow {
  scorecard_id: number;
  recipe_id: number;
  workspace_id: number | null;
  task_tag: string;
  receipt_count: number;
  avg_cost_micros: number;
  avg_latency_ms: number;
  acceptance_rate: number;
  correction_rate: number;
  ci_pass_rate: number;
  watcher_quietness: number;
  last_updated_at: number;
}

export interface ComparisonRow {
  recipe_id: number;
  task_tag: string;
  avg_cost_micros: number;
  avg_latency_ms: number;
  acceptance_rate: number;
  correction_rate: number;
  ci_pass_rate: number;
  watcher_quietness: number;
  receipt_count: number;
}

export async function listTaskTags(
  workspaceId: number,
): Promise<TaskTagRow[]> {
  return request(`/workspaces/${workspaceId}/task-tags`);
}

export async function attachTaskTag(
  workspaceId: number,
  body: {
    subject_kind: string;
    subject_id: number;
    tag: string;
    source: string;
  },
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/task-tags`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function listOutcomeTags(
  workspaceId: number,
): Promise<OutcomeTagRow[]> {
  return request(`/workspaces/${workspaceId}/outcome-tags`);
}

export async function attachOutcomeTag(
  workspaceId: number,
  body: {
    subject_kind: string;
    subject_id: number;
    outcome: string;
    source: string;
  },
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/outcome-tags`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function listShadowRuns(
  workspaceId: number,
): Promise<ShadowRunRow[]> {
  return request(`/workspaces/${workspaceId}/shadow-runs`);
}

export async function createShadowRun(
  workspaceId: number,
  body: {
    source_kind: string;
    source_id: number;
    team_recipe: unknown;
    persona_contract_versions: unknown;
    model_versions: unknown;
    harness: string;
    budget_micros: number;
  },
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/shadow-runs`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function listRecipeScorecards(
  workspaceId: number,
): Promise<RecipeScorecardRow[]> {
  // Backend returns SDB SqlResult; column order matches the SELECT in
  // shadow_routes::list_recipe_scorecards.
  const raw = await request<SqlResult>(
    `/workspaces/${workspaceId}/recipe-scorecards`,
  );
  return extractRows(raw).map((r) => ({
    scorecard_id: Number(r[0] ?? 0),
    recipe_id: Number(r[1] ?? 0),
    // recipe_scorecard.workspace_id is Option<U64> — null = org-wide.
    workspace_id: decodeOptionNumber(r[2]),
    task_tag: String(r[3] ?? ""),
    receipt_count: Number(r[4] ?? 0),
    avg_cost_micros: Number(r[5] ?? 0),
    avg_latency_ms: Number(r[6] ?? 0),
    acceptance_rate: Number(r[7] ?? 0),
    correction_rate: Number(r[8] ?? 0),
    ci_pass_rate: Number(r[9] ?? 0),
    watcher_quietness: Number(r[10] ?? 0),
    last_updated_at: Number(r[11] ?? 0),
  }));
}

export async function comparisonMetrics(
  workspaceId: number,
  taskTag?: string,
): Promise<ComparisonRow[]> {
  const qs = taskTag ? `?task_tag=${encodeURIComponent(taskTag)}` : "";
  return request(`/workspaces/${workspaceId}/comparison-metrics${qs}`);
}

export async function recommendRecipeForStep(
  workspaceId: number,
  recipeId: number,
  stepId: number,
): Promise<{
  recipe_id: number;
  step_id: number;
  matched_tags: string[];
  scorecards: unknown;
  rationale: string;
}> {
  return request(
    `/workspaces/${workspaceId}/recipes/${recipeId}/recommend-for-step/${stepId}`,
  );
}

// =====================================================================
// A23 — Harness Observability + Runner MVP
// =====================================================================

export interface RunnerSessionRow {
  session_id: number;
  executor_lane: string;
  harness_version: string;
  turn_id: number | null;
  status: string;
  last_heartbeat_at: number;
  created_at: number;
  completed_at: number | null;
}

export interface HarnessScoreRow {
  score_id: number;
  workspace_id: number | null;
  executor_lane: string;
  harness_version: string;
  task_tag: string;
  evidence_completeness: number;
  setup_friction: number;
  cost_visibility: number;
  command_control: number;
  artefact_capture: number;
  avg_latency_ms: number;
  failure_recovery: number;
  sample_count: number;
  last_updated_at: number;
}

export async function listRunnerSessions(
  workspaceId: number,
): Promise<RunnerSessionRow[]> {
  return request(`/workspaces/${workspaceId}/runner-sessions`);
}

export async function listHarnessScores(
  workspaceId: number,
): Promise<HarnessScoreRow[]> {
  // Backend returns SDB SqlResult; column order matches the SELECT in
  // runner_routes::list_harness_scores.
  const raw = await request<SqlResult>(
    `/workspaces/${workspaceId}/harness-scores`,
  );
  return extractRows(raw).map((r) => ({
    score_id: Number(r[0] ?? 0),
    // harness_score.workspace_id is Option<U64> — null = org-wide.
    workspace_id: decodeOptionNumber(r[1]),
    executor_lane: String(r[2] ?? ""),
    harness_version: String(r[3] ?? ""),
    task_tag: String(r[4] ?? ""),
    evidence_completeness: Number(r[5] ?? 0),
    setup_friction: Number(r[6] ?? 0),
    cost_visibility: Number(r[7] ?? 0),
    command_control: Number(r[8] ?? 0),
    artefact_capture: Number(r[9] ?? 0),
    avg_latency_ms: Number(r[10] ?? 0),
    failure_recovery: Number(r[11] ?? 0),
    sample_count: Number(r[12] ?? 0),
    last_updated_at: Number(r[13] ?? 0),
  }));
}

// =====================================================================
// A24 — Decision Archaeology + Replay
// =====================================================================

export interface DecisionRow {
  decision_id: number;
  road_id: number | null;
  agenda_id: number | null;
  step_id: number | null;
  kind: string;
  title: string;
  body_md: string;
  evidence_refs_json: string;
  author_agent_id: number | null;
  author_kind: string;
  design_archaeology: boolean;
  superseded_by: number | null;
  created_at: number;
}

export interface DriftAlertRow {
  alert_id: number;
  kind: string;
  summary: string;
  evidence_refs_json: string;
  status: string;
  created_at: number;
  resolved_at: number | null;
}

export async function listDecisions(
  workspaceId: number,
  opts?: { kind?: string; designArchaeology?: boolean; limit?: number },
): Promise<DecisionRow[]> {
  const params = new URLSearchParams();
  if (opts?.kind) params.set("kind", opts.kind);
  if (opts?.designArchaeology) params.set("design_archaeology", "true");
  if (opts?.limit) params.set("limit", String(opts.limit));
  const qs = params.toString();
  // The backend returns the raw SDB SqlResult shape (one table, rows
  // are positional arrays). Column order matches the SELECT in
  // archaeology_routes::list_decisions.
  const raw = await request<SqlResult>(
    `/workspaces/${workspaceId}/decisions${qs ? `?${qs}` : ""}`,
  );
  // SDB Option<U64> columns (road_id / agenda_id / step_id /
  // author_agent_id / superseded_by on `decision`) come back as
  // [0,value] or [1,[]] — flat Number(...) gave NaN. decodeOptionNumber
  // unwraps the variant correctly.
  return extractRows(raw).map((r) => ({
    decision_id: Number(r[0] ?? 0),
    road_id: decodeOptionNumber(r[1]),
    agenda_id: decodeOptionNumber(r[2]),
    step_id: decodeOptionNumber(r[3]),
    kind: String(r[4] ?? ""),
    title: String(r[5] ?? ""),
    body_md: String(r[6] ?? ""),
    evidence_refs_json: String(r[7] ?? "[]"),
    author_agent_id: decodeOptionNumber(r[8]),
    author_kind: String(r[9] ?? ""),
    design_archaeology: Boolean(r[10] ?? false),
    superseded_by: decodeOptionNumber(r[11]),
    created_at: Number(r[12] ?? 0),
  }));
}

export async function recordDecision(
  workspaceId: number,
  body: {
    road_id?: number;
    agenda_id?: number;
    step_id?: number;
    kind: string;
    title: string;
    body_md: string;
    evidence_refs: unknown;
    author_agent_id?: number;
    author_kind: string;
    design_archaeology: boolean;
  },
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/decisions`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function supersedeDecision(
  workspaceId: number,
  decisionId: number,
  body: {
    title: string;
    body_md: string;
    evidence_refs: unknown;
    author_agent_id?: number;
    author_kind: string;
  },
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/decisions/${decisionId}/supersede`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export async function decisionEvidence(
  workspaceId: number,
  decisionId: number,
): Promise<{ decision: unknown; evidence_refs: unknown }> {
  return request(
    `/workspaces/${workspaceId}/decisions/${decisionId}/evidence`,
  );
}

export interface AskTheLedgerResponse {
  answer_md: string;
  evidence_refs: unknown;
  uncertainty: string;
  suggested_next_action: string;
}

export async function askTheLedger(
  workspaceId: number,
  body: { question: string; road_id?: number; agenda_id?: number },
): Promise<AskTheLedgerResponse> {
  return request(`/workspaces/${workspaceId}/ask-the-ledger`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function replayAgenda(
  workspaceId: number,
  agendaId: number,
): Promise<{
  agenda_id: number;
  steps: unknown;
  decisions: unknown;
  watcher_fires: unknown;
  receipts: unknown;
}> {
  return request(`/workspaces/${workspaceId}/replay/agendas/${agendaId}`);
}

export async function listDriftAlerts(
  workspaceId: number,
): Promise<DriftAlertRow[]> {
  // Backend returns SDB SqlResult; column order matches the SELECT in
  // archaeology_routes::list_drift_alerts.
  const raw = await request<SqlResult>(
    `/workspaces/${workspaceId}/drift-alerts`,
  );
  return extractRows(raw).map((r) => ({
    alert_id: Number(r[0] ?? 0),
    kind: String(r[1] ?? ""),
    summary: String(r[2] ?? ""),
    evidence_refs_json: String(r[3] ?? "[]"),
    status: String(r[4] ?? ""),
    created_at: Number(r[5] ?? 0),
    // resolved_at is Option<I64> — same Sum encoding as Option<U64>.
    resolved_at: decodeOptionNumber(r[6]),
  }));
}

export async function resolveDriftAlert(
  workspaceId: number,
  alertId: number,
): Promise<{ ok: boolean }> {
  return request(
    `/workspaces/${workspaceId}/drift-alerts/${alertId}/resolve`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

// =====================================================================
// A25 — Newsfeed + Public Findings
// =====================================================================

export interface FindingRow {
  finding_id: number;
  workspace_id: number | null;
  org_id: number | null;
  scope: string;
  claim: string;
  task_tags_json: string;
  evidence_refs_json: string;
  aggregate_count: number;
  confidence: number;
  visibility: string;
  created_by_agent_id: number | null;
  created_at: number;
  revisit_at: number | null;
  superseded_by: number | null;
}

export interface NewsItemRow {
  news_id: number;
  kind: string;
  source: string;
  headline: string;
  body_md: string;
  url: string | null;
  task_tags_json: string;
  created_at: number;
}

export interface ModelReleaseRow {
  release_id: number;
  provider: string;
  model_id: string;
  display_name: string;
  context_window: number;
  price_input_micros_per_mtok: number;
  price_output_micros_per_mtok: number;
  capability_flags_json: string;
  seen_at: number;
  queued_for_shadow: boolean;
}

export interface PublicFindingRow {
  claim: string;
  task_tags_json: string;
  aggregate_count: number;
  confidence: number;
  created_at: number;
}

export async function listFindings(
  workspaceId: number,
  opts?: { includeOrg?: boolean; limit?: number },
): Promise<FindingRow[]> {
  const params = new URLSearchParams();
  if (opts?.includeOrg !== undefined)
    params.set("include_org", String(opts.includeOrg));
  if (opts?.limit) params.set("limit", String(opts.limit));
  const qs = params.toString();
  return request(`/workspaces/${workspaceId}/findings${qs ? `?${qs}` : ""}`);
}

export async function recordFinding(
  workspaceId: number,
  body: {
    scope: "workspace" | "org";
    claim: string;
    task_tags: string[];
    evidence_refs: unknown;
    aggregate_count: number;
    confidence: number;
    visibility: "private" | "org" | "public";
    created_by_agent_id?: number;
    revisit_at?: number;
  },
): Promise<{ ok: boolean }> {
  return request(`/workspaces/${workspaceId}/findings`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function listNewsItems(): Promise<NewsItemRow[]> {
  return request(`/news-items`);
}

export async function listModelReleases(): Promise<ModelReleaseRow[]> {
  return request(`/model-releases`);
}

export async function listPublicFindings(): Promise<PublicFindingRow[]> {
  return request(`/public/findings`, { auth: false });
}

// ── A28 / A29 / A31 — Tiering surface (billing + entitlements + savings) ──

export interface SubscriptionInfo {
  tier: "free" | "premium" | string;
  status: string;
  customer_id: string;
  subscription_id: string;
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
}

export interface Entitlements {
  tier: "free" | "premium";
  pooled_models_available: boolean;
  daily_compute_minutes_cap: number;
  daily_compute_minutes_used: number;
  persona_slot_limit: number;
  persona_slots_used: number;
  marketplace_access: boolean;
  warm_pool_priority: boolean;
  sandbox_interactive: boolean;
  road_view_visible: boolean;
  pearl_persona_craft_visible: boolean;
  shadow_teams_enabled: boolean;
  active_workspace_concurrency_cap: number;
  subscription_status: string | null;
  workspace_paused: boolean;
}

export interface SavingsWindow {
  real_micros: number;
  counterfactual_micros: number;
  savings_micros: number;
}

export interface SavingsResponse {
  today: SavingsWindow;
  this_week: SavingsWindow;
  this_month: SavingsWindow;
  all_time: SavingsWindow;
}

export async function getBillingSubscription(): Promise<SubscriptionInfo> {
  return request(`/billing/subscription`);
}

export async function createBillingCheckout(): Promise<{ checkout_url: string }> {
  return request(`/billing/checkout`, { method: "POST", body: "{}" });
}

export async function createBillingPortal(): Promise<{ portal_url: string }> {
  return request(`/billing/portal`, { method: "POST", body: "{}" });
}

export async function getEntitlements(workspaceId: number): Promise<Entitlements> {
  return request(`/workspaces/${workspaceId}/entitlements`);
}

export async function getSavings(workspaceId: number): Promise<SavingsResponse> {
  return request(`/workspaces/${workspaceId}/savings`);
}
